import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Theme, Typography, useMediaQuery } from '@mui/material';
import { useSnackbar } from 'notistack';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { PLAN_CODE_YEARLY, SUCCESS_OPERATION_ACTION_TYPE_SUBSCRIPTION_CHANGE } from 'constants/payment';
import { ISubscriptionContext } from 'context';
import { BackButton, DiscountInput, Info, SeatsCountSelector, Spinner } from 'components';
import { IApiRequests, useApiRequests, useSubscription } from 'hooks';
import {
  ApiPlan,
  ApiUser,
  ComputeSubscriptionProrationMode,
  ComputeSubscriptionProrationResponse,
  Coupon,
  GetPlanResponse,
  SubscriptionProrationApi,
  UpdateSubscriptionWithIncreasingAmountOfLicensesResponse,
} from 'types';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { getIsAdminByRoles } from 'utils';
import { getIntlLocale } from 'utils';

import styles from './UpgradeToAnnualPlanPage.module.scss';

export function UpgradeToAnnualPlanPage() {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const { t, i18n } = useTranslation('settings');
  const navigate = useNavigate();

  const subscription: ISubscriptionContext | null = useSubscription();
  const { getPlan, updateSubscriptionWithIncreasingAmountOfLicenses, computeSubscriptionProration }: IApiRequests =
    useApiRequests();
  const user: ApiUser = useAppSelector(selectUser)!;

  const [loading, setLoading] = useState<boolean>(true);
  const [seatsCount, setSeatsCount] = useState<number>(subscription?.totalSeats ?? 0);
  const [annualyPlan, setAnnualyPlan] = useState<ApiPlan | null>(null);
  const [subscriptionProration, setSubscriptionProration] = useState<SubscriptionProrationApi | null>(null);
  const [coupon, setCoupon] = useState<Coupon | null>(null);

  const locale = getIntlLocale(i18n.resolvedLanguage || i18n.language || 'en');

  useEffect(() => {
    if (subscription) {
      setSeatsCount(subscription.totalSeats);
    }
  }, [subscription]);

  useEffect(() => {
    if (
      !isMobile ||
      user.clinicUser.subscriptionInfo?.planCode === PLAN_CODE_YEARLY ||
      !getIsAdminByRoles(user.clinicUser.roles)
    ) {
      navigate('/');
      return;
    }

    getPlan(PLAN_CODE_YEARLY)
      .then((response: GetPlanResponse) => {
        setAnnualyPlan(response.plan);
      })
      .catch(() => {
        enqueueSnackbar(t('subscriptionNotifications.plansFetchError'), { variant: 'error' });
        navigate('/');
      })
      .finally(() => setLoading(false));
  }, [
    enqueueSnackbar,
    getPlan,
    isMobile,
    navigate,
    t,
    user.clinicUser.roles,
    user.clinicUser.subscriptionInfo?.planCode,
  ]);

  useEffect(() => {
    if (annualyPlan) {
      setLoading(true);

      computeSubscriptionProration({
        plan: { code: PLAN_CODE_YEARLY, quantity: seatsCount },
        mode: ComputeSubscriptionProrationMode.ProrateExistingSubscription,
        couponCode: coupon?.code,
      })
        .then((response: ComputeSubscriptionProrationResponse) => {
          setSubscriptionProration(response.subscriptionProration);
        })
        .catch((error) => {
          enqueueSnackbar(t('subscriptionNotifications.calculationError', { message: error.message }), {
            variant: 'error',
          });
          setSubscriptionProration(null);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [annualyPlan, computeSubscriptionProration, coupon, enqueueSnackbar, seatsCount, t]);

  const cardExparationString: string = useMemo(() => {
    const year = user.clinicUser.subscriptionInfo?.cardExpirationYear ?? 2022;
    const month = user.clinicUser.subscriptionInfo?.cardExpirationMonth ?? 1;
    return new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' }).format(new Date(year, month - 1, 1));
  }, [locale, user]);

  const pricePerOneSeatPerMonth: string = useMemo(() => {
    return ((annualyPlan?.recurringPrice ?? 0) / 12).toFixed(2);
  }, [annualyPlan]);

  const subtotalString: string = useMemo(() => {
    return '$' + ((annualyPlan?.recurringPrice ?? 0) * seatsCount).toFixed(2);
  }, [annualyPlan, seatsCount]);

  const renewsDate: string = useMemo(() => {
    const nextYear = new Date(user.clinicUser.subscriptionInfo?.serverTime ?? Date.now());
    nextYear.setFullYear(nextYear.getFullYear() + 1);
    return t('subscriptionPages.renews', {
      date: new Intl.DateTimeFormat(locale, { month: 'long', day: 'numeric', year: 'numeric' }).format(nextYear),
    });
  }, [t, user, locale]);

  const handleBackToSubscriptionsClick = (): void => {
    navigate('/settings#subscriptions');
  };

  const handleDecreaseSeat = (): void => {
    if (seatsCount > 1) {
      setSeatsCount(seatsCount - 1);
    }
  };

  const handleIncreaseSeat = (): void => {
    setSeatsCount(seatsCount + 1);
  };

  const handleApplyCoupon = (coupon: Coupon): void => {
    setCoupon(coupon);
  };

  const handleRemoveCoupon = (): void => {
    setCoupon(null);
  };

  const handleUpgrade = (): void => {
    setLoading(true);

    updateSubscriptionWithIncreasingAmountOfLicenses({
      planCode: PLAN_CODE_YEARLY,
      quantity: seatsCount,
      couponCode: coupon?.code,
      redirectUrl: `${process.env.REACT_APP_PUBLIC_URI}/successPayment?type=${SUCCESS_OPERATION_ACTION_TYPE_SUBSCRIPTION_CHANGE}&redirectTo=settings&redirectToHash=subscriptions`,
    })
      .then((response: UpdateSubscriptionWithIncreasingAmountOfLicensesResponse) => {
        window.location.href = response.page.url;
      })
      .catch((error) => {
        enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      {loading && <Spinner />}

      <div className={styles.page}>
        <BackButton text={t('subscriptionPages.backToSubscription')} onClick={handleBackToSubscriptionsClick} />

        <div className={styles.container}>
          <Box className={styles.containerTop}>
            <Typography variant='body4' component='span'>
              {t('subscriptionPages.upgradeToAnnualPlan')}
            </Typography>

            <Typography variant='body2' component='span'>
              {t('subscriptionPages.annualCreditLine1')}
            </Typography>

            <Typography variant='body2' component='span'>
              {t('subscriptionPages.annualCreditLine2', {
                date: new Intl.DateTimeFormat(locale, {
                  month: '2-digit',
                  day: '2-digit',
                  year: '2-digit',
                }).format(new Date(user.clinicUser.subscriptionInfo?.serverTime ?? Date.now())),
              })}
            </Typography>

            <div className={styles.devider}></div>

            <div className={styles.row}>
              <Typography variant='body2' component='span'>
                {t('subscriptionPages.paymentMethod')}
              </Typography>

              <div className={styles.card}>
                <Typography variant='body3' component='span' color='#393534'>
                  {t('subscriptionPages.cardEndingIn', {
                    digits: user.clinicUser.subscriptionInfo?.cardLastFourDigits ?? '0000',
                  })}
                </Typography>

                <Typography variant='body5' component='span' color='#393534'>
                  {t('subscriptionPages.expires', { date: cardExparationString })}
                </Typography>
              </div>
            </div>
          </Box>

          <Box className={styles.containerBottom}>
            <div className={classnames(styles.row, styles.pricePerMonth)}>
              <Typography variant='h4' component='h4'>
                {t('subscriptionPages.monthSeatRate', { price: pricePerOneSeatPerMonth })}
              </Typography>

              <Info text={t('subscriptionPages.seatsInfo')} tooltipClassName={styles.tooltip} />

              <Typography variant='body5' className={styles.billed} component='span' color='#626363'>
                {t('subscriptionPages.billedPerSeatAnnually', {
                  amount: (annualyPlan?.recurringPrice ?? 0).toFixed(2),
                })}
              </Typography>
            </div>

            <div className={styles.row}>
              <Typography variant='body2' component='span'>
                {t('subscriptionPages.totalSeats')}
              </Typography>

              <SeatsCountSelector
                count={seatsCount}
                decreaseDisabled={seatsCount <= (subscription?.totalSeats ?? 0) - (subscription?.availableSeats ?? 0)}
                onDecrease={handleDecreaseSeat}
                onIncrease={handleIncreaseSeat}
              />
            </div>

            <div className={styles.row}>
              <Typography variant='body2' component='span'>
                {t('subscriptionPages.discountCode')}
              </Typography>

              <DiscountInput onApply={handleApplyCoupon} onRemove={handleRemoveCoupon} plan={PLAN_CODE_YEARLY} />
            </div>

            <div className={styles.devider}></div>

            <div className={styles.row}>
              <div className={styles.calculationsRow}>
                <Typography variant='body5' component='span' color='#393534'>
                  {t('subscriptionPages.subtotal')}
                </Typography>

                <Typography variant='body5' component='span' color='#393534'>
                  {subtotalString}
                </Typography>
              </div>

              <div className={styles.calculationsRow}>
                <Typography variant='body5' component='span' color='#393534'>
                  {t('subscriptionPages.discount')}
                </Typography>

                <Typography variant='body5' component='span' color='#393534'>
                  -${subscriptionProration?.initialDiscount.toFixed(2)}
                </Typography>
              </div>

              <div className={styles.calculationsRow}>
                <Typography variant='body5' component='span' color='#393534'>
                  {t('subscriptionPages.total')}
                </Typography>

                <Typography variant='body5' component='span' color='#393534'>
                  ${subscriptionProration?.initialChargeTotal.toFixed(2)}
                </Typography>
              </div>

              <div className={styles.calculationsRow}>
                <Typography variant='body4' component='span'>
                  {t('subscriptionPages.proratedAmountDue')}
                </Typography>

                <Typography variant='body4' component='span'>
                  ${subscriptionProration?.initialChargeAmount.toFixed(2)}
                </Typography>
              </div>
            </div>

            <div className={styles.devider}></div>

            <div>
              <Typography variant='body5' component='span' color='#626363'>
                {renewsDate}
              </Typography>
            </div>

            <div className={styles.buttons}>
              <Button variant='contained' onClick={handleUpgrade}>
                {t('subscriptionPages.upgradeNow')}
              </Button>
            </div>
          </Box>
        </div>
      </div>
    </>
  );
}
